import React, { useState, useEffect } from "react"

// styles
import Styles from "./selectCurrencyModal.module.css"

// icons
import { ReactComponent as InfoIcon } from "../../../assets/icons/imported/info.svg"

// components
import Button from "../../../components/Button/Button"
import Search from "../../../components/Search/Search"
import WenbitModal from "../../../components/Modal/Modal"
import CoinTag from "../../../components/CoinTag/CoinTag"
import ShimmerLine from "../../../components/shimmer-components/ShimmerLine"
import ShimmerCircle from "../../../components/shimmer-components/ShimmerCircle"

// APIs
import { _getCurrencies, _getMarketsList } from "./redux/action"

// redux
import { useDispatch, useSelector } from "react-redux"
import { setSelectCurrencyModalOpen } from "./redux/selectCurrencyModalSlice"
import { randomizeShimmerWidth } from "../../../components/shimmer-components/helpers"
import { setDepositData } from "../../../redux/slices/depositSlice"

function SelectCurrencyModal() {
  const dispatch = useDispatch()

  const theme = useSelector((state) => state.theme.value)

  const [currencies, setCurrencies] = useState([])
  const [filteredCurrencies, setFilteredCurrencies] = useState(currencies)
  const [fiatCurrencies, setFiatCurrencies] = useState([])
  const [coinCurrencies, setCoinCurrencies] = useState([])

  const [markets, setMarkets] = useState([])
  const [filteredMarkets, setFilteredMarkets] = useState(markets)

  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")

  const modalOpen = useSelector((state) => state.selectCurrencyModalState.open)
  const modalData = useSelector((state) => state.selectCurrencyModalState.data)
  const [selectedTab, setSelectedTab] = useState(modalData.type)
  const marketArray = useSelector((state) => state.marketList.value)

  const handleClose = () => {
    dispatch(setSelectCurrencyModalOpen(false))
  }

  const fetchCurrencies = () => {
    setLoading(true)
    setCurrencies([])
    setFilteredCurrencies([])
    _getCurrencies(
      (data) => {
        // Temporarily disable fiat deposits and withdrawals by filtering out fiat currencies
        const nonFiatData = data.filter(currency => currency.type !== "fiat")
        
        setLoading(false)
        setCurrencies(nonFiatData)
        setFilteredCurrencies(nonFiatData)

        // Separate currencies by type
        const fiat = nonFiatData.filter(currency => currency.type === "fiat")
        const coins = nonFiatData.filter(currency => currency.type === "coin")

        setFiatCurrencies(fiat)
        setCoinCurrencies(coins)
      },
      {},
      (error) => {
        setLoading(false)
      }
    )
  }

  const fetchMarkets = () => {
    setLoading(true)
    _getMarketsList(
      (data) => {
        setLoading(false)
        setFilteredMarkets(data)
        setMarkets(data)
      },
      (err) => {
        console.log(err)
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    if (modalOpen) {
      console.log("Modal opened with data:", {
        availableCurrencies: modalData.availableCurrencies ?
          modalData.availableCurrencies.map(c => c.id).join(", ") : "none",
        type: modalData.type,
        title: modalData.title,
        showTabs: modalData.showTabs,
        currenciesToHide: modalData.currenciesToHide
      })

      if (modalData.showMarketPairs) {
        fetchMarkets()
      } else {
        fetchCurrencies()
      }

      // Auto-populate search with default selected market if provided
      if (modalData.defaultSelectedMarket && modalData.showMarketPairs) {
        const searchTerm = modalData.defaultSelectedMarket.base_unit || modalData.defaultSelectedMarket.coin_name || ''
        setSearchValue(searchTerm)
      }
    } else {
      // Reset search when modal closes
      setSearchValue("")
    }
  }, [selectedTab, modalOpen, modalData.defaultSelectedMarket])

  // Helper function to apply filtering based on available currencies
  const applyAvailableCurrenciesFilter = () => {
    const availableCurrencies = modalData.availableCurrencies

    console.log("⚠️ FILTER CHECK - Available currencies:", availableCurrencies ?
      availableCurrencies.map(c => c.id.toUpperCase()).join(", ") : "none")

    if (availableCurrencies && availableCurrencies.length > 0 && currencies.length > 0) {
      // Get the available currency IDs
      const availableCurrencyIds = availableCurrencies.map(c => c.id)

      // Log which currencies we're filtering to
      console.log("⚠️ FILTER CHECK - Will filter to these IDs:", availableCurrencyIds.join(", "))

      // Do the filtering
      const allCurrencies = currencies.map(c => c.id)
      console.log("⚠️ FILTER CHECK - All currencies:", allCurrencies.join(", "))

      // Filter both coins and fiat currencies
      const filteredCurrencies = currencies.filter(
        currency => availableCurrencyIds.includes(currency.id)
      )

      console.log("⚠️ FILTER CHECK - Filtered to:", filteredCurrencies.map(c => c.id).join(", "))

      // Split by type and set state
      const filteredCoins = filteredCurrencies.filter(currency => currency.type === "coin")
      const filteredFiat = filteredCurrencies.filter(currency => currency.type === "fiat")

      setCoinCurrencies(filteredCoins)
      setFiatCurrencies(filteredFiat)
      setFilteredCurrencies(filteredCurrencies)

      return true
    }
    return false
  }

  // Call the filter function when modal data changes (particularly availableCurrencies)
  useEffect(() => {
    if (modalOpen && currencies.length > 0) {
      console.log("Modal data changed:", {
        availableCurrenciesCount: modalData.availableCurrencies ? modalData.availableCurrencies.length : 0
      })

      if (modalData.availableCurrencies && modalData.availableCurrencies.length > 0) {
        console.log("Got availableCurrencies! Applying filter...")
        setTimeout(() => applyAvailableCurrenciesFilter(), 0)
      } else {
        // Apply default filtering by selected tab
        if (selectedTab === "all") {
          setFilteredCurrencies(currencies)
          setCoinCurrencies(currencies.filter(currency => currency.type === "coin"))
          setFiatCurrencies(currencies.filter(currency => currency.type === "fiat"))
        } else if (selectedTab === "coin") {
          const temp = currencies.filter(currency => currency.type === "coin")
          setFilteredCurrencies(temp)
          setCoinCurrencies(temp)
          setFiatCurrencies([])
        } else if (selectedTab === "fiat") {
          const temp = currencies.filter(currency => currency.type === "fiat")
          setFilteredCurrencies(temp)
          setCoinCurrencies([])
          setFiatCurrencies(temp)
        }
      }
    }
  }, [modalData, modalOpen, currencies.length])

  useEffect(() => {
    if (modalData.showMarketPairs) {
      // For market pairs, filter markets
      if (searchValue) {
        const tempArray = markets.filter(
          (el) =>
            el?.base_unit
              ?.toLowerCase()
              .includes(searchValue?.toLowerCase()) ||
            el?.id?.toLowerCase().includes(searchValue?.toLowerCase())
        )
        setFilteredMarkets(tempArray)
      } else {
        setFilteredMarkets(markets)
      }
    } else {
      // For currencies, use existing logic
      if (searchValue) {
        filterCurrencies(searchValue)
      } else {
        // If no search term provided, use filterCurrencies with empty string to apply availableCurrencies filter
        filterCurrencies("")
      }
    }
  }, [searchValue, currencies, markets, modalData.availableCurrencies, modalData.showMarketPairs])

  const filterCurrencies = (searchTerm) => {
    // First apply available currencies filter if present
    let baseFilteredCurrencies = currencies

    console.log("Filter currencies called with:", {
      searchTerm,
      availableCurrencies: modalData.availableCurrencies ?
        (modalData.availableCurrencies.length + " items") : "none"
    })

    // If available currencies are provided, filter to those first
    if (modalData.availableCurrencies && modalData.availableCurrencies.length > 0) {
      const availableCurrencyIds = modalData.availableCurrencies.map(c => c.id)
      console.log("Available currency IDs in filter:", availableCurrencyIds)
      baseFilteredCurrencies = currencies.filter(currency => availableCurrencyIds.includes(currency.id))
      console.log("Filtered to available currencies:", baseFilteredCurrencies.map(c => c.id))
    }

    if (!searchTerm) {
      // If no search term, just use the base filtered currencies
      // Split into fiat and coin categories
      const filteredFiat = baseFilteredCurrencies.filter(currency => currency.type === "fiat")
      const filteredCoins = baseFilteredCurrencies.filter(currency => currency.type === "coin")

      console.log("Filtered currencies (no search):", {
        fiat: filteredFiat.length,
        coins: filteredCoins.length,
        total: baseFilteredCurrencies.length
      })

      setFiatCurrencies(filteredFiat)
      setCoinCurrencies(filteredCoins)
      setFilteredCurrencies(baseFilteredCurrencies)
      return
    }

    // Apply search filter on top of available currencies
    const searchResults = baseFilteredCurrencies.filter(
      el =>
        el?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        el?.id?.toLowerCase().includes(searchTerm?.toLowerCase())
    )

    // Split search results by currency type
    const searchResultCoins = searchResults.filter(currency => currency.type === "coin")
    const searchResultFiat = searchResults.filter(currency => currency.type === "fiat")

    console.log("Search results:", {
      fiat: searchResultFiat.length,
      coins: searchResultCoins.length,
      total: searchResults.length
    })

    // Set all the filtered currencies
    setFilteredCurrencies(searchResults)
    setCoinCurrencies(searchResultCoins)
    setFiatCurrencies(searchResultFiat)
  }

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value
    setSearchValue(searchTerm)

    if (modalData.showMarketPairs) {
      const tempArray = markets.filter(
        (el) =>
          el?.base_unit
            ?.toLowerCase()
            .includes(searchTerm?.toLowerCase()) ||
          el?.id?.toLowerCase().includes(searchTerm?.toLowerCase())
      )
      setFilteredMarkets(tempArray)
    } else {
      filterCurrencies(searchTerm)
    }
  }

  const handleRowClick = (currency) => {
    dispatch(setDepositData(currency))
    modalData.callback(currency)
    handleClose()
  }

  const renderCurrencyRow = (currency) => {
    if (modalData?.currenciesToHide?.includes(currency.id)) {
      return null
    }

    return (
      <div
        className={`${Styles.row} ${theme === "light" && Styles.lightRow}`}
        key={currency?.id}
        onClick={() => {
          handleRowClick(currency)
        }}
      >
        {currency.icon_url && <img src={currency?.icon_url} alt={currency.name} />}
        <span>{currency?.name}</span>
        <CoinTag tag={currency?.id?.toUpperCase()} />
      </div>
    )
  }

  const renderCurrenciesList = () => {
    if (loading) {
      return new Array(7).fill(0).map((_, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "center",
            padding: 7,
            gap: 15,
            margin: "0px 0"
          }}
        >
          <ShimmerCircle size={35} />
          <ShimmerLine height={35} width={randomizeShimmerWidth()} />
        </div>
      ))
    }

    if (modalData.showMarketPairs) {
      return filteredMarkets?.length ? (
        filteredMarkets?.map((market) => (
          <div
            key={market?.id}
            className={`${Styles.marketRow} ${theme === "light" && Styles.lightRow}`}
            onClick={() => {
              handleRowClick(market)
            }}
          >
            <img src={market?.base_url} alt={market.name} />
            <span>{market?.name.toUpperCase()}</span>
            <CoinTag tag={market?.base_coin_name} />
          </div>
        ))
      ) : (
        <div className={Styles.notFound}>
          <InfoIcon width={12} />
          Market not found
        </div>
      )
    }

    // Check if there are any currencies to show at all
    if (filteredCurrencies.length === 0) {
      return (
        <div className={Styles.notFound}>
          <InfoIcon width={12} />
          No currencies found
        </div>
      )
    }

    // Determine what sections to show based on whether we have currencies of each type
    const showFiat = fiatCurrencies.length > 0
    console.log(fiatCurrencies)
    const showCoins = coinCurrencies.length > 0

    console.log("Rendering currency sections:", {
      showFiat,
      showCoins,
      fiatCount: fiatCurrencies.length,
      coinCount: coinCurrencies.length
    })

    return (
      <>
        {/* Fiat section */}
        {showFiat && (
          <div className={Styles.currencySection}>
            <div className={Styles.sectionHeader}>Fiat</div>
            {fiatCurrencies.map(renderCurrencyRow)}
          </div>
        )}

        {/* Divider between sections - only show if both sections have content */}
        {showFiat && showCoins && (
          <div className={Styles.sectionDivider}></div>
        )}

        {/* Coins section */}
        {showCoins && (
          <div className={Styles.currencySection}>
            <div className={Styles.sectionHeader}>Coins</div>
            {coinCurrencies.map(renderCurrencyRow)}
          </div>
        )}
      </>
    )
  }

  return (
    <WenbitModal
      open={modalOpen}
      onClose={handleClose}
      title={modalData.title}
      showCloseButton={true}
      disableBackdropClick={modalData?.disableBackdropClick}
      render={() => (
        <div>
          {modalData.showTabs && (
            <div className={Styles.tabsContainer}>
              <Button
                text="Crypto"
                type="tab"
                style={{
                  flex: 1,
                  fontSize: 16
                }}
                onClick={() => {
                  if (loading) return
                  setSelectedTab("coin")
                }}
                isTabSelected={selectedTab === "coin"}
              />
              <Button
                text="Fiat"
                type="tab"
                style={{
                  flex: 1,
                  fontSize: 16,
                  color: "#888", // Darker grey text
                  cursor: "not-allowed", // Prevent pointer interaction
                  opacity: 0.3, // Faded look
                  pointerEvents: "none", // Disable clicking
                }}
                disabled={true}
                onClick={() => {
                  // Fiat tab disabled
                }}
                isTabSelected={selectedTab === "fiat"}
              />
            </div>
          )}

          <Search
            placeholder={modalData.searchPlaceholder ?? "Search coin"}
            containerStyle={{ width: "100%" }}
            onChange={handleSearchChange}
            value={searchValue}
            autoFocus
          />

          <div className={`${Styles.rowsContainer}`}>
            {renderCurrenciesList()}
          </div>
        </div>
      )}
    />
  )
}

export default SelectCurrencyModal